import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  makeStyles,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import { Field } from 'formik';
import FormikTextFieldWithClearError from 'components/FormikFields/FormikTextFieldWithClearError';
import { FormattedPhone } from 'components/Shared/FormattedPhone';
import { useSelector } from 'react-redux';
import cx from 'classnames';

const useStyles = makeStyles((theme) => ({
  title: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  dataContainer: {
    width: '100%',
    maxWidth: 1400,
  },
  checkbox: {
    margin: theme.spacing(0, 0, 2.25, 0),
  },
  smallLabelName: {
    '& span': {
      fontSize: 14,
    },
  },
}));

const BasicInfoSection = ({
                            tradeName,
                            businessName,
                            backendErrors,
                            isEditMode,
                            isCurrentAccountEditing,
                            isDistributionSponsor,
                            clearBackendError,
                            disabled = false,
                          }) => {
  const classes = useStyles();
  const { en: { forms } } = useSelector((state) => state.i18n);
  const [tradeNameCheckBox, setTradeNameCheckBox] = useState(false);

  useEffect(() => {
    setTradeNameCheckBox(tradeName === businessName);
  }, []);

  return (
    <>
      <Field name='isDistributionSponsor' type='checkbox'>
        {({ field, form }) => (
          <FormControlLabel
            control={
              <Checkbox
                name={field.name}
                checked={field.value}
                color='primary'
                onChange={(e, checked) => {
                  form.setFieldValue(field.name, checked);
                  if (clearBackendError) {
                    clearBackendError('isDistributionSponsor');
                  }
                }}
                disabled={disabled || isEditMode}
              />
            }
            className={classes.checkbox}
            label={forms.distributionSponsor}
          />
        )}
      </Field>

      <Grid container spacing={3} className={classes.dataContainer}>
        <Grid item xs={12} md={6} lg={4}>
          <FormikTextFieldWithClearError
            type='text'
            name='businessName'
            label={forms.businessName}
            backendError={backendErrors.businessName}
            required
            duplicateValueToFieldName={tradeNameCheckBox ? 'tradeName' : null}
            disabled={disabled || !isDistributionSponsor}
            clearBackendError={() => {
              clearBackendError('businessName')
              if (tradeNameCheckBox) {
                clearBackendError('tradeName');
              }
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.dataContainer}>
        <Grid
          item
          xs={12}
          container
          direction='row'
          alignItems='center'
          spacing={3}
        >
          <Grid item xs={12} md={6} lg={4}>
            <FormikTextFieldWithClearError
              type='text'
              name='tradeName'
              label={forms.tradeName}
              backendError={backendErrors.tradeName}
              required
              disabled={disabled || tradeNameCheckBox}
              clearBackendError={clearBackendError}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4} mb={2}>
            <Field name='sameAsBusinessName' type='checkbox'>
              {({ form }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tradeNameCheckBox}
                      color='primary'
                      disabled={disabled}
                      onChange={(e, checked) => {
                        setTradeNameCheckBox(checked);
                        form.setFieldValue('tradeName', checked ? form.values.businessName : '');
                        if (clearBackendError) {
                          clearBackendError('tradeName');
                        }
                      }}
                    />
                  }
                  style={{ maxWidth: 150 }}
                  className={cx(classes.checkbox, classes.smallLabelName)}
                  label={forms.sameAsBusinessName}
                />
              )}
            </Field>
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.dataContainer}>
        <Grid item xs={12} md={8} lg={6}>
          <FormikTextFieldWithClearError
            type='text'
            name='contactName'
            label={forms.contactName}
            backendError={backendErrors.contactName}
            disabled={disabled}
            clearBackendError={clearBackendError}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} className={classes.dataContainer}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <FormikTextFieldWithClearError
            type='tel'
            name='phone'
            label={forms.phone}
            backendError={backendErrors.phone}
            InputProps={{
              inputComponent: FormattedPhone,
            }}
            required
            disabled={disabled}
            clearBackendError={clearBackendError}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <FormikTextFieldWithClearError
            type='tel'
            name='fax'
            label={forms.fax}
            backendError={backendErrors.fax}
            InputProps={{
              inputComponent: FormattedPhone,
            }}
            disabled={disabled}
            clearBackendError={clearBackendError}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.dataContainer}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <FormikTextFieldWithClearError
            type='text'
            name='headProviderName'
            label={forms.headProviderName}
            backendError={backendErrors.headProviderName}
            disabled={disabled}
            clearBackendError={clearBackendError}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <FormikTextFieldWithClearError
            type='text'
            name='customIdentifier'
            label={forms.customId}
            backendError={backendErrors.customIdentifier}
            disabled={disabled || isCurrentAccountEditing}
            clearBackendError={clearBackendError}
          />
        </Grid>
      </Grid>
    </>
  );
};

BasicInfoSection.propTypes = {
  tradeName: PropTypes.string,
  businessName: PropTypes.string,
  isCurrentAccountEditing: PropTypes.bool,
  isDistributionSponsor: PropTypes.bool,
  clearBackendError: PropTypes.func,
  disabled: PropTypes.bool,
};

BasicInfoSection.defaultProps = {
  tradeName: '',
  businessName: '',
  isCurrentAccountEditing: false,
  isDistributionSponsor: false,
};

export default BasicInfoSection;
